import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import * as React from 'react';

export type ChatMode = 'conversation' | 'search';

export const DEFAULT_CHAT_MODE: ChatMode = 'conversation';

type CoachChatContextType = {
  // Chat mode management
  chatMode: ChatMode;
  setChatMode: (mode: ChatMode) => void;
  isConversationMode: () => boolean;

  // Find-in-chat search functionality
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;
  isSearchMode: () => boolean;
  enterSearchMode: (searchTerm?: string) => void;
  exitSearchMode: () => void;
};

const CoachChatContext = createContext<CoachChatContextType | undefined>(undefined);

type CoachChatContextProviderProps = {
  children: ReactNode;
};

export const CoachChatContextProvider = ({ children }: CoachChatContextProviderProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [chatMode, setChatMode] = useState<ChatMode>(DEFAULT_CHAT_MODE);

  const clearSearch = useCallback<CoachChatContextType['clearSearch']>(() => {
    setSearchTerm('');
  }, []);

  const enterSearchMode = useCallback<CoachChatContextType['enterSearchMode']>((initialSearchTerm = '') => {
    setChatMode('search');

    if (initialSearchTerm) {
      setSearchTerm(initialSearchTerm);
    }
  }, []);

  const exitSearchMode = useCallback<CoachChatContextType['exitSearchMode']>(() => {
    setChatMode(DEFAULT_CHAT_MODE);
    clearSearch();
  }, [clearSearch]);

  const contextValue: CoachChatContextType = {
    chatMode,
    setChatMode,
    isConversationMode: () => chatMode === 'conversation',
    searchTerm,
    setSearchTerm,
    clearSearch,
    isSearchMode: () => chatMode === 'search',
    enterSearchMode,
    exitSearchMode,
  };

  return <CoachChatContext.Provider value={contextValue}>{children}</CoachChatContext.Provider>;
};

export const useCoachChatContext = (): CoachChatContextType => {
  const context = useContext(CoachChatContext);
  if (context === undefined) {
    throw new Error('useCoachChatContext must be used within a CoachChatContextProvider');
  }
  return context;
};
